import * as cmFwuTargetTypes from '../../consts/communicationLayer/cmFwuTargetTypes';
import * as cmMessageErrorCodes from '../../consts/communicationLayer/cmMessageErrorCodes';
import * as cmMessageProperties from '../../consts/communicationLayer/cmMessageProperties';
import * as cmMessageTypes from '../../consts/communicationLayer/cmMessageTypes';
import cmTimeouts from '../../consts/config/cmTimeouts';
import {BLE_STACK_FWU_RECONNECT_POPUP_DELAY_MS} from '../../consts/consts';
import * as firmwareErrorCodes from '../../consts/iot/firmwareErrorCodes';
import * as fwuPackageTypes from '../../consts/iot/fwuPackageTypes';
import {getScpFrameRequestPriorityByCmPriorityLevel} from '../../enums/iot/cmPriorityLevelTypes';
import FWU_TARGET_TYPES from '../../enums/iot/fwuTargetTypes';
import IOT_DEVICE_TARGET_NAMES from '../../enums/iot/iodDeviceTargetNames';
import {setFWUError} from '../../state/ducks/iotDevice/actions';
import {setMwDeviceFirmwareData} from '../../state/ducks/mwDevice';
import {setCmMessageData, setYapFirmwareInProgress, setYapLastRequestId} from '../../state/ducks/yapEncrypted';
import {selectIsFirmwareCompleted, selectIsFirmwareTargetBleStack} from '../../state/selectors/iotDevice';
import {makeSelectIsOfflineYapFirmwareInProgress} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import deviceCharacteristicService from '../ble/deviceCharacteristicService';
import connectivityService from '../device/connectivityService';
import deviceIdlePingService from '../device/deviceIdlePingService';
import framesProcessingService from '../device/framesProcessingService';
import fwuService from '../device/fwuService';
import fwuTargetService from '../device/fwuTargetService';
import cyclicFramesService from '../hid/cyclicFramesService';
import appRouterService from '../route/appRouterService';
import {scpUtils} from '../scp/scpUtils';
import cmMessageRequestService from './cmMessageRequestService';
import cmService from './cmService';

const onStatusCharacteristicHandler = (message) => {
    const isBle = connectivityService.isBle();

    if (isBle) {
        onStatusCharacteristicBleHandler(message);
    } else {
        onStatusCharacteristicUsbHandler(message);
    }
};

const onStatusCharacteristicBleHandler = (message) => {
    const {data} = message;
    const properties = [cmMessageProperties.SYSTEM_STATUS, cmMessageProperties.BATTERY_STATUS];

    properties.forEach((statusPropertyName) => {
        const propertyValue = data?.[statusPropertyName];
        const {subscribe: uuid} = propertyValue || {};

        if (!uuid) return;

        deviceCharacteristicService.subscribeToCharacteristic(statusPropertyName, uuid);
    });
};

const onStatusCharacteristicUsbHandler = (message) => {
    const {type, request_id, data} = message;
    const properties = [cmMessageProperties.SYSTEM_STATUS, cmMessageProperties.BATTERY_STATUS];

    properties.forEach((statusPropertyName) => {
        const propertyValue = data?.[statusPropertyName];
        const {iteration_delay, frame} = propertyValue || {};

        if (!frame || !iteration_delay) return;

        const props = {
            type,
            cyclicId: statusPropertyName,
            frames: [frame],
            request_id,
            cyclicTimeout: iteration_delay,
        };

        cyclicFramesService.writeFrameCyclic(props);
    });
};

const onCycleRequestHandler = (message) => {
    const {type, token, request_id, data} = message;
    const {iteration_delay, cycle_frame, max_iterations} = data || {};

    if (!cycle_frame || !iteration_delay) return;

    const props = {
        token,
        type,
        cyclicId: cmMessageProperties.CYCLE_FRAME,
        frames: [cycle_frame],
        request_id,
        cyclicTimeout: iteration_delay,
        maxIterations: max_iterations,
        isResponseCheckNeeded: true,
    };

    cyclicFramesService.writeFrameCyclic(props);
};

const onSuccessPackageLoad = (fwu_target, message) => {
    const {token, request_id} = message;
    const isHolderRelated = fwuTargetService.isFwuTargetHolderRelated(fwu_target);

    cmMessageRequestService.publishFwuPackageLoadedEvent(request_id, token, isHolderRelated);
};

const onFwuError = (params, message, isHolder) => {
    const state = getState();
    const isOfflineMode = makeSelectIsOfflineYapFirmwareInProgress()(state);

    if (isOfflineMode) {
        dispatch(setFWUError());
        dispatch(setYapLastRequestId(null, isHolder));
        dispatch(setYapFirmwareInProgress(false));
        return;
    }

    const {type, token, request_id} = message;
    const {fwu_target} = params;
    const isHolderRelated = fwuTargetService.isFwuTargetHolderRelated(fwu_target);

    cmMessageRequestService.publishFwuErrorEvent({
        type,
        request_id,
        token,
        isHolder: isHolderRelated,
        ...params,
    });
};

const getIsEngineFromMessage = (message) => {
    const {data} = message;
    const {target} = data || {};
    const isEngine = target === cmFwuTargetTypes.ENGINE;

    return isEngine;
};

const getFwuTargetFromMessage = (message, isHolder) => {
    const isEngine = getIsEngineFromMessage(message);
    const isBleStack = fwuPackageTypes.getIsPackageTypeBleStack(message?.data?.package_type);

    if (isHolder) {
        return isEngine ? FWU_TARGET_TYPES.HOLDER_ENGINE : FWU_TARGET_TYPES.HOLDER_DEVICE;
    }

    if (isBleStack) {
        return FWU_TARGET_TYPES.BLE_STACK;
    }

    return isEngine ? FWU_TARGET_TYPES.MONO_ENGINE : FWU_TARGET_TYPES.CHARGER_OR_MONO;
};

const storeFirmwareDataToMwState = (message, isHolder) => {
    const {data} = message;
    const isEngine = getIsEngineFromMessage(message);

    let fwData;

    if (isHolder) {
        fwData = isEngine ? {[IOT_DEVICE_TARGET_NAMES.HOLDER_ENGINE]: data} : {[IOT_DEVICE_TARGET_NAMES.HOLDER]: data};
    } else {
        const isBleStack = fwuPackageTypes.getIsPackageTypeBleStack(data?.package_type);

        if (isBleStack) {
            fwData = {[IOT_DEVICE_TARGET_NAMES.BLE_STACK]: data};
        } else {
            fwData = isEngine
                ? {[IOT_DEVICE_TARGET_NAMES.DEVICE_ENGINE]: data}
                : {[IOT_DEVICE_TARGET_NAMES.DEVICE]: data};
        }
    }

    dispatch(setMwDeviceFirmwareData(fwData));
};

const onFirmwareUpdatePackageLoadHandler = async (message, isHolder) => {
    const {data} = message;

    storeFirmwareDataToMwState(message, isHolder);
    fwuService.clearPackageData();

    const fwu_target = getFwuTargetFromMessage(message, isHolder);

    await fwuService.downloadFwuPackage(
        fwu_target,
        (fwu_target) => {
            onSuccessPackageLoad(fwu_target, message);
        },
        (params) => {
            onFwuError(params, message, isHolder);
        }
    );

    const isSblFirmware = fwuPackageTypes.getIsPackageTypeSbl(data.package_type);

    appRouterService.forwardToUpdateFWPage({
        target: fwu_target,
        version: data.version,
        isSblFirmware,
    });
};

const onFirmwareUpdateStartHandler = async (message, isHolder) => {
    const {type, token, request_id} = message;

    const onFwuStatusEvent = (n) => {
        cmMessageRequestService.publishFwuStatusEvent(type, request_id, token, n, isHolder);
    };
    const onFwuFinish = (isHolder) => {
        dispatch(setCmMessageData(token, request_id));
        cmMessageRequestService.publishFwuFinishEvent(type, request_id, token, isHolder);
    };

    const isConnected = await cmService.waitUntilConnected({isHolder, isCheckDeviceReady: true});
    const fwu_target = getFwuTargetFromMessage(message, isHolder);

    if (isConnected) {
        await fwuService.startFwUpdate({
            fwu_target,
            onFwuError: (params) => onFwuError(params, message, isHolder),
            onFwuStatusEvent,
            onFwuFinish,
        });
    } else {
        onFwuError({errorCode: firmwareErrorCodes.GENERAL, fwu_target}, message, isHolder);
    }
};

const defaultHandler = async (message, isHolder) => {
    const {type, token, request_id, frames, data, conditional_frames, priority_level} = message;

    if (!frames?.length && !conditional_frames) return;

    const onWriteFramesError = (isDeviceDisconnect) => {
        if (!isHolder && isDeviceDisconnect && type === cmMessageTypes.RESET) {
            cmMessageRequestService.publishSuccessReset({request_id, token});
            return;
        }

        cmMessageRequestService.publishErrorEvent({
            type,
            request_id,
            token,
            errorCode: isDeviceDisconnect ? cmMessageErrorCodes.DEVICE_DISCONNECTED : cmMessageErrorCodes.UNKNOWN_ERROR,
            isHolder,
        });
        deviceIdlePingService.pingDeviceIfNeeded();
    };
    const writeFrames = ({frames, framesNodeName = 'frames'}) => {
        framesProcessingService.writeFrameAndPublishResponse({
            frames,
            skipErrorOnTimeout: type === cmMessageTypes.DEVICE_USAGE_DATA_CAPABILITIES,
            onSuccess: (...frames) => {
                cmMessageRequestService.publishResponseEvent({
                    type,
                    request_id,
                    token,
                    isHolder,
                    data,
                    [framesNodeName]: frames,
                });
            },
            onError: onWriteFramesError,
            priority: getScpFrameRequestPriorityByCmPriorityLevel(priority_level),
            requestId: request_id,
        });
    };

    if (conditional_frames) {
        const {initial_frame} = conditional_frames;

        framesProcessingService.writeFrameAndPublishResponse({
            frames: [initial_frame],
            onSuccess: (frame) => {
                const {
                    mask_check: {offset, mask},
                    if_no_match_frames,
                    if_match_frames,
                } = conditional_frames;
                const maskParsed = parseInt(mask);
                const doesFrameMatchMask = scpUtils.doesFrameMatchMask({frame, offset, mask: maskParsed});
                const writeFramesProps = doesFrameMatchMask
                    ? {frames: if_match_frames, framesNodeName: 'match_frames'}
                    : {frames: if_no_match_frames, framesNodeName: 'no_match_frames'};

                writeFrames(writeFramesProps);
            },
            onError: onWriteFramesError,
        });

        return;
    }

    if (type === cmMessageTypes.SOFTWARE_IDENTIFICATION || type === cmMessageTypes.SBL_IDENTIFICATION) {
        const state = getState();
        const isOfflineMode = makeSelectIsOfflineYapFirmwareInProgress()(state);

        if (isOfflineMode) return;

        const isReady = await cmService.waitUntilConnected({
            isHolder,
            isCheckDeviceReady: true,
            timer: getFwuReconnectTimeout(),
        });

        if (!isReady) {
            const fwu_target = getFwuTargetFromMessage(message, isHolder);

            onFwuError({errorCode: cmMessageErrorCodes.DEVICE_DISCONNECTED, fwu_target}, message, isHolder);
            return;
        }
    }

    writeFrames({frames});
};

const onCancelRequestHandler = (message, isHolder) => {
    const {type, token, request_id, data} = message;
    const {cancel_request_id} = data || {};

    if (!cancel_request_id) return;

    const onCancel = (success) => {
        cmMessageRequestService.publishResponseEvent({
            type,
            request_id,
            token,
            isHolder,
            data: {
                success,
            },
        });
    };

    framesProcessingService.cancelRequest({requestId: cancel_request_id, onCancel});
};

const getFwuReconnectTimeout = () => {
    const isFirmwareTargetBleStack = selectIsFirmwareTargetBleStack(getState());
    const isFirmwareCompleted = selectIsFirmwareCompleted(getState());
    const result =
        cmTimeouts.CM_FWU_CONNECT_TIMEOUT +
        (isFirmwareTargetBleStack && isFirmwareCompleted ? BLE_STACK_FWU_RECONNECT_POPUP_DELAY_MS / 100 : 0);

    return result;
};

export default {
    onStatusCharacteristicHandler,
    onCycleRequestHandler,
    onFirmwareUpdatePackageLoadHandler,
    onFirmwareUpdateStartHandler,
    defaultHandler,
    onCancelRequestHandler,
};
